import { CLAUSE_CLASSIFICATIONS, CLAUSE_TYPES } from '@root/helpers/clauses/constants';
import { DEFAULT_VARIATION_ID, OLD_DEFAULT_VARIATION_ID } from '@root/helpers/variations/constants';
import { z } from 'zod';

import type { PK } from './database.schema';
import { baseDatabaseSchema, systemUserSchema, userSchema } from './database.schema';

export const BlockVisibilityConfigSchema = z
  .object({
    conditions: z.array(
      z.object({
        field: z.string(),
        operator: z.enum(['IS', 'IS NOT', 'IS EMPTY', 'IS NOT EMPTY']),
        value: z.union([z.string(), z.array(z.string()), z.number(), z.boolean()]).optional(), // is this correct?
      }),
    ),
    match: z.enum(['AND', 'OR']).optional(),
    mode: z.enum(['SHOW', 'HIDE', 'AUTOSELECT']),
  })
  .strict();

export const BlockVariationVisibilityConfigSchema = BlockVisibilityConfigSchema.extend({
  id: z.string(),
}).strict();

/** A visibility config as `helpers/variations` evaluates it: each condition carries its result. */
export const BlockVisibilityResultSchema = BlockVisibilityConfigSchema.extend({
  conditions: z.array(BlockVisibilityConfigSchema.shape.conditions.element.extend({ result: z.boolean() })),
});

/** The evaluated visibility of a block or one of its variations. */
export const BlockVisibilityOutputSchema = BlockVisibilityResultSchema.extend({
  result: z.boolean(),
  info: z.string().optional(),
  id: z.string().optional(),
});

/** A datapoint linked from a block or variation's content; `isRequired` gates publishing. */
export const LinkedDatapointSchema = z.object({ id: z.string(), isRequired: z.boolean() });

/** One variation of a block: same content model as the block itself, selected per contract via `selectedVariationId`. */
export const BlockVariationSchema = z.object({
  id: z.string(),
  title: z.string(),
  content: z.string(),
  linkedDatapoints: z.array(LinkedDatapointSchema),
  repeaterIds: z.array(z.string()),
  // to be removed
  name: z.string().optional(), // why do we need this?
  isComplete: z.boolean().optional(), // what is this?
  created_by: userSchema.optional(), // why do we need this?
});

export const LibraryBlockSchema = z.object({
  // required fields
  name: z.string(),
  // defaulted fields
  content: z.string().default(''),
  type: z.enum(['clause', 'clause-heading', 'generic', 'mrc-heading', 'attachment']).default('mrc-heading'),
  canEditOnTemplate: z.boolean().default(true),
  canEdit: z.boolean().default(true),
  canDelete: z.boolean().default(true),
  canDeleteOnTemplate: z.boolean().default(true).optional(),
  shouldInsertPageBreak: z.boolean().default(false),
  linkedDatapoints: z.array(LinkedDatapointSchema).default([]),
  repeaterIds: z.array(z.string()).default([]),
  variations: z.array(BlockVariationSchema).default([]),
  // optional fields
  class_of_business: z.array(z.string()).optional(),
  classification: z.enum(CLAUSE_CLASSIFICATIONS).optional(),
  teams: z.array(z.union([z.string(), z.number()])).optional(), // number for PRO-clients and string for PPL
  seedId: z.string().uuid().optional(),
  parentBlockId: z.string().optional(),
  deleted_at: z.string().nullable().optional(),
  title: z.string().optional(),
  clause_reference: z.string().optional(),
  clause_type: z.union([z.enum(CLAUSE_TYPES), z.literal('')]).optional(),
  helperText: z.string().optional(),
  usedInTemplates: z.array(z.object({ id: z.string(), name: z.string().optional() })).optional(),
  // to be removed
  systemHelperText: z.array(z.string()).optional(), // what is this?
  order: z.number().optional(),
  mrc_heading_type: z.string().optional(),
  copied_from_template_id: z.string().optional(),
  isVisible: z.boolean().optional(),
  isComplete: z.boolean().optional(), // what is this?
  // class_of_business: z.string().optional(), // what is this?
  // commentsCount: z.number().optional(),
  expiry_date: z.nullable(z.string()).optional(),
  inception_date: z.nullable(z.string()).optional(),
  attachment_id: z.string().optional(),
  last_imported_at: z.string().optional(),
});

export const TemplateBlockSchema = z.object({
  // required fields
  section_id: z.string(),
  blockLibraryId: z.string(),
  // optional fields
  canDeleteOnTemplate: z.boolean().default(true),
  selectedVariationId: z
    .union([z.string().uuid(), z.literal(DEFAULT_VARIATION_ID), z.literal(OLD_DEFAULT_VARIATION_ID)])
    .optional(),
  visibility: z
    .object({
      block: BlockVisibilityConfigSchema.optional(),
      variations: z.array(z.union([BlockVariationVisibilityConfigSchema, z.null()])).optional(),
    })
    .optional(),
});

export const SubmissionBlockSchema = LibraryBlockSchema.merge(TemplateBlockSchema)
  .extend({
    /** Server-managed: set once, the first time a library-sourced clause's rendered content is edited. */
    amended_at: z.string().optional(),
    amended_by: userSchema.partial({ tenant_id: true }).or(systemUserSchema).optional(),
  })
  .partial({ blockLibraryId: true });

export const DbLibraryBlockSchema = LibraryBlockSchema.merge(baseDatabaseSchema);
export const DbTemplateBlockSchema = TemplateBlockSchema.merge(baseDatabaseSchema);
export const DbSubmissionBlockSchema = SubmissionBlockSchema.merge(baseDatabaseSchema);

export const DbLibraryBlockFilteredSchema = DbLibraryBlockSchema.pick({
  pk: true,
  sk: true,
  canDelete: true,
  canEdit: true,
  canDeleteOnTemplate: true,
  canEditOnTemplate: true,
  repeaterIds: true,
  variations: true,
  class_of_business: true,
  classification: true,
  clause_reference: true,
  clause_type: true,
  created_at: true,
  deleted_at: true,
  expiry_date: true,
  id: true,
  inception_date: true,
  name: true,
  parentBlockId: true,
  teams: true,
  title: true,
  type: true,
  updated_at: true,
  usedInTemplates: true,
}).extend({
  last_updated_by: userSchema.pick({ name: true }).optional(),
  created_by: userSchema.pick({ name: true }),
});

export const OpenSearchBlockSchema = DbLibraryBlockSchema.pick({
  name: true,
  title: true,
  type: true,
  clause_type: true,
  clause_reference: true,
  classification: true,
});

export type BlockType = z.infer<typeof LibraryBlockSchema>['type'];
export type BlockVariation = z.infer<typeof BlockVariationSchema>;
export type LinkedDatapoint = z.infer<typeof LinkedDatapointSchema>;
export type BlockVisibility = NonNullable<z.infer<typeof TemplateBlockSchema>['visibility']>;
export type BlockLevelVisibilityConfig = z.infer<typeof BlockVisibilityConfigSchema>;
export type BlockVisibilityCondition = BlockLevelVisibilityConfig['conditions'][number];
export type BlockVisibilityOperators = BlockVisibilityCondition['operator'];
export type BlockVisibilityValueType = NonNullable<BlockVisibilityCondition['value']>;
export type VariationLevelVisibilityConfig = z.infer<typeof BlockVariationVisibilityConfigSchema>;
export type BlockVisibilityResult = z.infer<typeof BlockVisibilityResultSchema>;
export type BlockVisibilityOutput = z.infer<typeof BlockVisibilityOutputSchema>;
export type DbLibraryBlock = z.infer<typeof DbLibraryBlockSchema> & PK;
export type DbTemplateBlock = z.infer<typeof DbTemplateBlockSchema> & PK;
export type DbSubmissionBlock = z.infer<typeof DbSubmissionBlockSchema> & PK;
export type DbContractBlock = DbTemplateBlock | DbSubmissionBlock;
export type DbLibraryBlockFiltered = z.infer<typeof DbLibraryBlockFilteredSchema> & PK;
export type OpenSearchBlock = z.infer<typeof OpenSearchBlockSchema>;
